import type * as Highcharts from 'highcharts';
import { REPORT_CHART_LIMITS, REPORT_CHARTS } from '../constants/reportCharts';
import { REPORT_PACKAGE_ORDER, reportChartLabel, reportLabels, reportPackageLabels } from '../constants/reportLabels';
import type {
  DateValue,
  NamedSolution,
  PackageComparisonResult,
  PackageSolutionGroupResult,
  ReportChartId,
  ReportContext,
  ReportPackageKey,
  SoilingAssessmentResult,
  SolutionChartKey,
  SolutionPackageKey,
} from '../types';
import type { CapturedChart, ChartJob } from './chartCapture';
import {
  ALIGNED_CHART_MARGIN_LEFT,
  ALIGNED_CHART_MARGIN_RIGHT,
  ALIGNED_CHART_TICK_INTERVAL,
  buildCleaningSchedulesOptions,
} from './chartOptions/cleaningSchedules';
import { buildFinancialComparisonOptions } from './chartOptions/financialComparison';
import { buildNpvHeatmapOptions } from './chartOptions/npvHeatmap';
import { buildPieOptions } from './chartOptions/pie';
import { buildProductionLossesOptions } from './chartOptions/productionLosses';
import { productionChartData, soilingRatioChartData } from './chartOptions/solutionSeries';
import { buildSoilingRiskOptions } from './chartOptions/soilingRisk';
import { buildTimeSeriesOptions, type TimeSeriesChartData } from './chartOptions/timeSeries';
import { buildTypicalSoilingYearOptions } from './chartOptions/typicalSoilingYear';
import { getSolutionDisplayName } from './utils';

const FOR_EXPORT = { forExport: true };
const SOILING_RATIO_RANGE = { ymin: 0.4, ymax: 1.2 };
// The ex-post screen shows no CAPEX/OPEX breakdown, so neither does its report section.
const EX_POST_HIDDEN_CHARTS: SolutionChartKey[] = ['capex', 'opex'];

/** Saved project inputs the charts are drawn with, as on screen. */
export interface ReportChartInputs {
  /** `results.solution` of the saved project. */
  savedSolution: string | undefined;
  yearlyCleaningDays: DateValue[] | undefined;
  rainThreshold: number | undefined;
  /** Cleaning packages catalogue, for the display names of the solution groups. */
  cleaningPackages: Record<string, { name?: string }>;
}

export interface ReportChartPlan {
  jobs: ChartJob[];
  warnings: string[];
}

type SolutionChartBuilders = Record<SolutionChartKey, () => Highcharts.Options | null>;

/**
 * Days without cleaning left out of the cleaning columns: their empty column lies below the soiling ratio axis, clipped
 * out of the plot, yet costs an element per day to the report.
 */
const withoutEmptyCleanings = (data: TimeSeriesChartData): TimeSeriesChartData => {
  const cleaning = data.values.cleaning;
  return cleaning ? { ...data, values: { ...data.values, cleaning: cleaning.map((value) => (value === 0 ? null : value)) } } : data;
};

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const hasDailyTimes = (series: unknown) => isObject(series) && isObject(series.daily) && Array.isArray(series.daily.times);

const isAssessmentResult = (result: unknown): result is SoilingAssessmentResult =>
  isObject(result) && isObject(result.scores) && hasDailyTimes(result.soiling);

const isPackageComparisonResult = (result: unknown): result is PackageComparisonResult =>
  isObject(result) &&
  hasDailyTimes(result.data) &&
  isObject(result.package_solution_groups) &&
  Object.keys(result.package_solution_groups).length > 0;

const optimalSolution = (name: string, group: PackageSolutionGroupResult): NamedSolution => ({
  name,
  ...group.solutions[group.optimal_index],
});

/** NPV of the optimal solution of a group, missing or non-finite values ranking last. */
const groupNpv = (group: PackageSolutionGroupResult) => {
  const npv = group.solutions[group.optimal_index]?.net_present_value;
  return typeof npv === 'number' && Number.isFinite(npv) ? npv : -Infinity;
};

/** The saved solution group when the result has it, else the max-NPV group (first one on a tie), as the server does. */
export const reportedGroupKey = (groups: Record<string, PackageSolutionGroupResult>, savedSolution: string | undefined): string => {
  const keys = Object.keys(groups);
  if (savedSolution && keys.includes(savedSolution)) return savedSolution;
  return keys.reduce((best, key) => (groupNpv(groups[key]) > groupNpv(groups[best]) ? key : best), keys[0]);
};

const assessmentJobs = (result: SoilingAssessmentResult, inputHash: string, inputs: ReportChartInputs): ChartJob[] => {
  const builders: Record<string, () => Highcharts.Options | null> = {
    'assessment.soiling_risk': () =>
      result.scores.rain != null && result.scores.deposition != null ? buildSoilingRiskOptions(result.scores, '', FOR_EXPORT) : null,
    'assessment.soiling_ratio': () =>
      buildTypicalSoilingYearOptions(result.soiling, {
        yearlyCleaningDays: inputs.yearlyCleaningDays,
        rainThreshold: inputs.rainThreshold,
        ...SOILING_RATIO_RANGE,
      }),
  };
  return REPORT_CHARTS.assessment.map((chartId) => ({ package: 'assessment', chartId, inputHash, build: builders[chartId] }));
};

/** Builders of the solution charts: the reported solution, then the comparison of all the groups, as on screen. */
const solutionChartBuilders = (result: PackageComparisonResult, inputs: ReportChartInputs): SolutionChartBuilders => {
  const groups = result.package_solution_groups;
  const key = reportedGroupKey(groups, inputs.savedSolution);
  const solution = optimalSolution(key, groups[key]);
  const reference = result.data.daily;
  // The screen only compares solutions when several groups are selected.
  const compared = Object.keys(groups).length > 1 ? Object.keys(groups).map((name) => optimalSolution(name, groups[name])) : null;
  const title = 'NPV Gain Heatmap for ' + getSolutionDisplayName(inputs.cleaningPackages, key);

  return {
    npv_heatmap: () => buildNpvHeatmapOptions(groups[key], title, FOR_EXPORT),
    production: () => buildTimeSeriesOptions(productionChartData(reference, [solution]), {}),
    soiling_ratio: () =>
      buildTimeSeriesOptions(withoutEmptyCleanings(soilingRatioChartData(result.rain_dates, reference, [solution])), {
        ...SOILING_RATIO_RANGE,
        datetimeAxis: true,
        marginLeft: ALIGNED_CHART_MARGIN_LEFT,
        marginRight: ALIGNED_CHART_MARGIN_RIGHT,
        tickInterval: ALIGNED_CHART_TICK_INTERVAL,
      }),
    capex: () => (solution.capex ? buildPieOptions(solution.capex, 'Cleaning CAPEX ($)', '$') : null),
    opex: () => (solution.opex ? buildPieOptions(solution.opex, 'Cleaning OPEX ($)', '$') : null),
    financial_comparison: () => (compared ? buildFinancialComparisonOptions(compared, FOR_EXPORT) : null),
    production_losses: () => (compared ? buildProductionLossesOptions(compared) : null),
    cleaning_schedules: () => (compared ? buildCleaningSchedulesOptions(compared, result.rain_dates) : null),
  };
};

const solutionJobs = (
  pkg: SolutionPackageKey,
  result: PackageComparisonResult,
  inputHash: string,
  inputs: ReportChartInputs,
): ChartJob[] => {
  const builders = solutionChartBuilders(result, inputs);
  const hidden = pkg === 'ex_post' ? EX_POST_HIDDEN_CHARTS : [];
  const skipNoComparison = Object.keys(result.package_solution_groups).length < 2;
  return REPORT_CHARTS[pkg]
    .map((chartId) => ({ chartId, key: chartId.split('.')[1] as SolutionChartKey }))
    .filter(({ key }) => !hidden.includes(key))
    .filter(({ key }) => !(skipNoComparison && ['financial_comparison', 'production_losses', 'cleaning_schedules'].includes(key)))
    .map(({ chartId, key }) => ({ package: pkg, chartId, inputHash, build: builders[key] }));
};

const packageJobs = (pkg: ReportPackageKey, result: unknown, inputHash: string, inputs: ReportChartInputs): ChartJob[] | null => {
  try {
    if (pkg === 'assessment') return isAssessmentResult(result) ? assessmentJobs(result, inputHash, inputs) : null;
    return isPackageComparisonResult(result) ? solutionJobs(pkg, result, inputHash, inputs) : null;
  } catch (error) {
    console.error(`Report charts of ${pkg} could not be planned`, error);
    return null;
  }
};

/**
 * Charts to draw for every up-to-date package of the context (fetched with its results), in catalogue order.
 * A package whose result cannot be read is left out with a warning.
 */
export const planReportCharts = (context: ReportContext, inputs: ReportChartInputs): ReportChartPlan => {
  const jobs: ChartJob[] = [];
  const warnings: string[] = [];
  REPORT_PACKAGE_ORDER.forEach((pkg) => {
    const packageContext = context.packages[pkg];
    if (packageContext?.status !== 'up_to_date' || !packageContext.input_hash) return;
    const planned = packageJobs(pkg, packageContext.result, packageContext.input_hash, inputs);
    if (planned) jobs.push(...planned);
    else warnings.push(reportLabels.package_results_unreadable(reportPackageLabels[pkg]));
  });
  return { jobs, warnings };
};

const submissionBytes = (chart: CapturedChart) => new TextEncoder().encode(JSON.stringify(chart.submission)).length;

/** Ids of the charts to leave out, the costliest first, so that the render cost of the others fits the server budget. */
const overBudgetCharts = (charts: CapturedChart[]): Set<ReportChartId> => {
  let total = charts.reduce((sum, chart) => sum + chart.stats.renderCost, 0);
  const dropped = new Set<ReportChartId>();
  [...charts]
    .sort((a, b) => b.stats.renderCost - a.stats.renderCost)
    .forEach((chart) => {
      if (total <= REPORT_CHART_LIMITS.maxReportRenderCost) return;
      dropped.add(chart.submission.chart_id);
      total -= chart.stats.renderCost;
    });
  return dropped;
};

/**
 * Charts kept within the server limits: the render budget over all the charts (the server would omit every chart past
 * it in catalogue order, here the costliest are left out first), the chart count, then the body size by leaving out
 * the largest charts first.
 */
export const fitChartsToLimits = (charts: CapturedChart[]): { kept: CapturedChart[]; warnings: string[] } => {
  const overBudget = overBudgetCharts(charts);
  const warnings = charts
    .filter((chart) => overBudget.has(chart.submission.chart_id))
    .map((chart) => reportLabels.chart_over_budget(reportChartLabel(chart.submission.chart_id)));
  const withinBudget = charts.filter((chart) => !overBudget.has(chart.submission.chart_id));

  const sized = withinBudget.slice(0, REPORT_CHART_LIMITS.maxCharts).map((chart) => ({ chart, bytes: submissionBytes(chart) }));
  let total = sized.reduce((sum, { bytes }) => sum + bytes + 1, 0);
  const dropped = new Set<ReportChartId>();
  [...sized]
    .sort((a, b) => b.bytes - a.bytes)
    .forEach(({ chart, bytes }) => {
      if (total <= REPORT_CHART_LIMITS.maxBodyBytes) return;
      dropped.add(chart.submission.chart_id);
      total -= bytes + 1;
    });
  const trimmed = withinBudget.length - sized.length + dropped.size;
  if (trimmed > 0) warnings.push(reportLabels.charts_trimmed(trimmed));
  return { kept: sized.filter(({ chart }) => !dropped.has(chart.submission.chart_id)).map(({ chart }) => chart), warnings };
};
